import { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Handles Supabase invite/recovery fragments landing on /socials*.
 *
 * Supabase's action_link redirects to `<site>/#access_token=...&type=invite`
 * (or `#error=access_denied&error_code=otp_expired`). React Router does not
 * consume the hash, and our route guard would otherwise bounce a valid
 * invited user to /socials/login without ever establishing the session.
 *
 * On mount:
 *  - If hash contains error=access_denied → /socials/login?invite_expired=1.
 *  - If hash contains access_token + refresh_token → setSession, strip the
 *    hash, and land on /socials/profile so the user can set a password.
 *  - Otherwise render children immediately.
 */
export function SocialsAuthFragmentBridge({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [processing, setProcessing] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const h = window.location.hash || "";
    return h.includes("access_token=") || h.includes("error=");
  });

  useEffect(() => {
    if (!processing) return;
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);

    const error = params.get("error");
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const type = params.get("type");

    (async () => {
      try {
        if (error) {
          // Wipe fragment + send to login with a friendly reason.
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          navigate("/socials/login?invite_expired=1", { replace: true });
          return;
        }
        if (accessToken && refreshToken) {
          const { error: setErr } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          window.history.replaceState(null, "", window.location.pathname + window.location.search);
          if (setErr) {
            navigate("/socials/login?invite_expired=1", { replace: true });
            return;
          }
          // Invited users land on profile so they can set a real password.
          if (type === "invite" || type === "recovery" || type === "signup") {
            navigate("/socials/profile?onboard=1", { replace: true });
            return;
          }
        }
      } finally {
        setProcessing(false);
      }
    })();
  }, [processing, navigate]);

  if (processing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }
  return <>{children}</>;
}

export default SocialsAuthFragmentBridge;
